import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useRecebi } from "@/hooks/use-mercado";
import { usePagarVenda } from "@/hooks/use-vendas";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { fmtMoedaBR, fmtDataBR } from "@/lib/formato-mercado";
import { checarDataRecebimento, mensagemDeFalhaDeData } from "@shared/mercado";
import { DESCRICAO_RECEBI } from "@shared/textos-pagamento";

export interface RomaneioParaReceber {
  /** A chave é a VENDA: nem toda linha de "A receber" foi formalizada. */
  vendaId: string;
  numero: string;
  comprador: string;
  valor: number;
  /** ISO "AAAA-MM-DD" — o dinheiro não entra antes de a carga sair. */
  dataVenda: string;
}

/**
 * "Recebi" / "Lançar recebimento" / "Marcar como paga" — os TRÊS caminhos.
 *
 * Com um romaneio só (o botão da linha, ou a venda no Cacho) confirma aquele;
 * com vários (o botão do header de A receber) abre o seletor entre os A_PAGAR.
 *
 * ⚠️ A DATA VALE. Ela era informativa — ia para o evento e para o audit log
 * enquanto `paga_em` recebia `now()` —, e é por `paga_em` que "Recebido no mês"
 * soma: marcar no dia 3 um dinheiro que entrou no dia 28 jogava o valor para o
 * mês errado, e o produtor só descobria ao fechar o mês. Desde a 061 ela grava.
 *
 * ⚠️ `via` escolhe a ROTA, não a tela. No Mercado o lançamento passa pela rota do
 * produtor (que sincroniza o compartilhamento e registra o evento); no Cacho, que
 * existe sem o módulo Mercado ligado, pela rota da venda. A tela, as regras da
 * data e a mensagem são as MESMAS — uma confirmação com duas caras seria a
 * segunda definição de "confirmar recebimento".
 */
export function DialogRecebi({
  romaneios,
  inicialId,
  via = "mercado",
  onFechar,
}: {
  romaneios: RomaneioParaReceber[];
  inicialId?: string;
  /** Qual rota confirma. A tela e as regras não mudam — ver o bloco acima. */
  via?: "mercado" | "cacho";
  onFechar: () => void;
}) {
  const { toast } = useToast();
  // Os dois hooks são chamados sempre (regra dos hooks); quem não é o `via` fica
  // parado, sem disparar requisição.
  const recebi = useRecebi();
  const pagar = usePagarVenda();
  const pendente = via === "mercado" ? recebi.isPending : pagar.isPending;
  const [id, setId] = useState(inicialId ?? romaneios[0]?.vendaId ?? "");
  const [data, setData] = useState(hojeNaFazenda());
  const escolhido = romaneios.find(r => r.vendaId === id);
  const fixo = !!inicialId;

  // Mesma checagem do servidor, da mesma função: o produtor vê o erro antes de
  // tocar em Confirmar, e não depois de um 422.
  const falha = escolhido ? checarDataRecebimento(data, escolhido.dataVenda, hojeNaFazenda()) : null;

  async function handleConfirmar() {
    if (!escolhido || falha) return;
    try {
      if (via === "cacho") {
        await pagar.mutateAsync({ id: escolhido.vendaId, data });
        toast({ title: `Romaneio ${escolhido.numero} recebido.`, description: `Recebimento em ${fmtDataBR(data)}.` });
      } else {
        const r = await recebi.mutateAsync({ vendaId: escolhido.vendaId, data });
        toast({
          title: r.jaEstavaPago ? "Este romaneio já estava pago." : `Romaneio ${escolhido.numero} recebido.`,
          description: r.jaEstavaPago ? undefined : "A venda no Cacho está marcada como paga.",
        });
      }
      onFechar();
    } catch (e: any) {
      toast({ title: "Não deu para lançar", description: e.message, variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{fixo ? `Recebi o romaneio ${escolhido?.numero ?? ""}` : "Lançar recebimento"}</DialogTitle>
          <DialogDescription>
            {DESCRICAO_RECEBI}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {fixo ? (
            <div className="rounded-lg border px-3 py-2.5 text-sm">
              <p className="font-semibold">{escolhido?.comprador}</p>
              <p className="text-xs text-muted-foreground">Nº {escolhido?.numero} · <b className="font-mono tabular-nums text-foreground">{fmtMoedaBR(escolhido?.valor ?? 0)}</b></p>
            </div>
          ) : romaneios.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum romaneio em aberto para lançar.</p>
          ) : (
            <div className="space-y-1.5">
              <Label>Romaneio</Label>
              <Select value={id} onValueChange={setId}>
                <SelectTrigger data-testid="select-recebi-romaneio"><SelectValue placeholder="Escolha o romaneio" /></SelectTrigger>
                <SelectContent>
                  {romaneios.map(r => (
                    <SelectItem key={r.vendaId} value={r.vendaId}>
                      {r.numero} · {r.comprador} · {fmtMoedaBR(r.valor)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="recebi-data">Data do recebimento</Label>
            <Input
              id="recebi-data"
              type="date"
              className="h-11"
              value={data}
              max={hojeNaFazenda()}
              min={escolhido?.dataVenda}
              onChange={e => setData(e.target.value)}
              data-testid="input-recebi-data"
            />
            {falha && (
              <p className="text-[11px] text-destructive" data-testid="erro-recebi-data">
                {mensagemDeFalhaDeData(falha, "recebimento")}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={handleConfirmar} disabled={!escolhido || !!falha || pendente} data-testid="button-recebi-confirmar">
            <Check className="h-4 w-4 mr-2" />
            {pendente ? "Lançando…" : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
